/**
 * 👥 Admin Staff Management Service
 * Handles admin user (staff) management endpoints
 */

import apiClient from './apiClient';

export interface AdminStaff {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  role: {
    id: string;
    name: string;
    type: 'system' | 'custom';
  };
  status: 'active' | 'suspended' | 'inactive';
  department?: string;
  createdAt: string;
  createdBy?: {
    id: string;
    email: string;
    fullName: string;
  };
  lastLoginAt?: string;
  customPermissions?: Permission[];
}

export interface Permission {
  id: string;
  resource: string;
  action: string;
  conditions?: string[];
}

export interface SystemRole {
  id: string;
  name: string;
  description: string;
  type: 'system' | 'custom';
  status: 'active' | 'inactive';
  userCount: number;
  permissions: Permission[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminStaffFilters {
  page?: number;
  limit?: number;
  status?: 'active' | 'suspended' | 'inactive' | 'all';
  role?: string;
  search?: string;
}

export interface AuditLog {
  id: string;
  adminUserId: string;
  adminEmail: string;
  adminRole: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  oldValue?: any;
  newValue?: any;
  metadata?: any;
  ipAddress?: string;
  userAgent?: string;
  status: 'success' | 'failed';
  errorMessage?: string;
  createdAt: string;
}

export const adminStaffService = {
  /**
   * Get all admin staff members (using new backend endpoint)
   */
  async getAdminStaff(filters?: AdminStaffFilters): Promise<{
    success: boolean;
    data?: {
      admins: AdminStaff[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      // Use the new backend endpoint: /api/v1/admin/staff/list
      const url = `/api/v1/admin/staff/list`;


      const response = await apiClient.get(url);


      if (response.data.success) {
        // Backend returns: { success: true, data: [...] }
        const staffList = Array.isArray(response.data.data) ? response.data.data : [];

        // Map backend field names to frontend expectations
        const mappedList = staffList.map((staff: any) => ({
          ...staff,
          lastLoginAt: staff.lastLogin || staff.lastLoginAt, // Backend returns lastLogin, we expect lastLoginAt
        }));

        // Apply client-side filtering if needed
        let filteredList = mappedList;

        if (filters?.status && filters.status !== 'all') {
          filteredList = filteredList.filter((staff: any) => staff.status === filters.status);
        }

        if (filters?.role && filters.role !== 'all') {
          filteredList = filteredList.filter((staff: any) => staff.role === filters.role);
        }

        if (filters?.search) {
          const searchLower = filters.search.toLowerCase();
          filteredList = filteredList.filter((staff: any) =>
            staff.email?.toLowerCase().includes(searchLower) ||
            staff.fullName?.toLowerCase().includes(searchLower)
          );
        }

        // Client-side pagination
        const page = filters?.page || 1;
        const limit = filters?.limit || 20;
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedList = filteredList.slice(startIndex, endIndex);

        return {
          success: true,
          data: {
            admins: paginatedList,
            pagination: {
              page,
              limit,
              totalCount: filteredList.length,
              totalPages: Math.ceil(filteredList.length / limit),
            }
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || response.data.error || 'Failed to fetch admin staff'
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching admin staff:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to fetch admin staff'
      };
    }
  },

  /**
   * Create new admin staff member (using new backend endpoint)
   */
  async createAdminStaff(data: {
    email: string;
    fullName: string;
    phone?: string;
    roleId: string;
    department?: string;
    password: string; // Required field
    generatePassword?: boolean;
  }): Promise<{
    success: boolean;
    data?: {
      admin: AdminStaff;
      temporaryPassword?: string;
      welcomeEmailSent?: boolean;
    };
    error?: string;
    errorCode?: string;
    errorDetails?: {
      email: string;
      status: string;
      adminId: string;
      suggestion: string;
    };
  }> {
    try {

      // Use new backend endpoint: /api/v1/admin/staff/create
      const requestBody = {
        email: data.email,
        fullName: data.fullName,
        role: data.roleId, // Backend expects role as string (e.g., 'admin', 'super_admin')
        password: data.password, // Required by backend
        ...(data.phone ? { phone: data.phone } : {}),
      };


      const response = await apiClient.post('/api/v1/admin/staff/create', requestBody);


      if (response.data.success) {
        return {
          success: true,
          data: {
            admin: response.data.data,
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to create admin staff',
        };
      }
    } catch (error: any) {
      console.error('❌ Error creating admin staff:', error);
      console.error('📋 DEBUG: Full error object:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });

      // Handle duplicate email error
      const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
      if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('already exists')) {
        return {
          success: false,
          error: 'An admin with this email already exists',
          errorCode: 'ADMIN_ALREADY_EXISTS'
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create admin staff'
      };
    }
  },

  /**
   * Update admin staff member (using new backend endpoint)
   */
  async updateAdminStaff(
    staffId: string,
    data: {
      fullName?: string;
      phone?: string;
      roleId?: string;
      department?: string;
      status?: 'active' | 'suspended' | 'inactive';
      password?: string;
    }
  ): Promise<{
    success: boolean;
    data?: AdminStaff;
    error?: string;
  }> {
    try {

      // Use new backend endpoint: /api/v1/admin/staff/update-{id}
      const requestBody: any = {};

      if (data.fullName) requestBody.fullName = data.fullName;
      // Phone (SMS alerts, SMS sign-in codes); the dialog starts from the saved number, empty clears it
      if (data.phone !== undefined) requestBody.phone = data.phone;
      if (data.roleId) requestBody.role = data.roleId; // Backend expects 'role' not 'roleId'
      if (data.status) requestBody.status = data.status;
      if (data.password) requestBody.password = data.password;

      const response = await apiClient.put(`/api/v1/admin/staff/update-${staffId}`, requestBody);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to update admin staff'
        };
      }
    } catch (error: any) {
      console.error('❌ Error updating admin staff:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update admin staff'
      };
    }
  },

  /**
   * Delete admin staff member
   */
  async deleteAdminStaff(
    staffId: string
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {

      const response = await apiClient.delete(`/api/v1/admin/staff/${staffId}`);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Admin staff deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to delete admin staff'
        };
      }
    } catch (error: any) {
      console.error('❌ Error deleting admin staff:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to delete admin staff'
      };
    }
  },

  /**
   * Suspend admin staff member
   */
  async suspendAdminStaff(
    staffId: string,
    reason?: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {

      // Use the update endpoint to change status to suspended
      const response = await apiClient.patch(`/api/v1/admin/staff/${staffId}`, {
        status: 'suspended'
      });

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to suspend admin staff'
        };
      }
    } catch (error: any) {
      console.error('❌ Error suspending admin staff:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to suspend admin staff'
      };
    }
  },

  /**
   * Reset admin staff password
   */
  async resetPassword(
    staffId: string,
    data: {
      sendEmail?: boolean;
    }
  ): Promise<{
    success: boolean;
    data?: {
      temporaryPassword?: string;
      resetEmailSent?: boolean;
    };
    error?: string;
  }> {
    try {

      const response = await apiClient.post(`/api/v1/admin/staff/${staffId}/reset-password`, data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to reset password'
        };
      }
    } catch (error: any) {
      console.error('❌ Error resetting password:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to reset password'
      };
    }
  },

  /**
   * Resend invitation to admin staff member
   */
  async resendInvitation(
    staffId: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      email: string;
      fullName: string;
      role: string;
      expiresInHours: number;
    };
    error?: string;
  }> {
    try {

      const response = await apiClient.post(`/api/v1/admin/staff/${staffId}/resend-invitation`);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to resend invitation'
        };
      }
    } catch (error: any) {
      console.error('❌ Error resending invitation:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to resend invitation'
      };
    }
  },

  /**
   * Update custom permissions for admin staff
   */
  async updatePermissions(
    staffId: string,
    permissions: Permission[]
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {

      const response = await apiClient.patch(`/api/v1/admin/staff/${staffId}/permissions`, {
        permissions
      });

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to update permissions'
        };
      }
    } catch (error: any) {
      console.error('❌ Error updating permissions:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to update permissions'
      };
    }
  },

  /**
   * Get all system roles with permissions
   * Note: Backend returns role strings, we normalize them to role objects
   */
  async getSystemRoles(): Promise<{
    success: boolean;
    data?: {
      roles: SystemRole[];
    };
    error?: string;
  }> {
    try {

      // Backend has predefined roles, return them as static list for now
      // Available roles: super_admin, admin, staff, support, manager
      const staticRoles: SystemRole[] = [
        {
          id: 'super_admin',
          name: 'Super Admin',
          description: 'Full system access with all permissions',
          type: 'system',
          status: 'active',
          userCount: 0,
          permissions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'admin',
          name: 'Admin',
          description: 'Administrative access with limited permissions',
          type: 'system',
          status: 'active',
          userCount: 0,
          permissions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'staff',
          name: 'Staff',
          description: 'Standard staff member access',
          type: 'system',
          status: 'active',
          userCount: 0,
          permissions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'support',
          name: 'Support',
          description: 'Customer support team access',
          type: 'system',
          status: 'active',
          userCount: 0,
          permissions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: 'manager',
          name: 'Manager',
          description: 'Department manager access',
          type: 'system',
          status: 'active',
          userCount: 0,
          permissions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];

      return {
        success: true,
        data: {
          roles: staticRoles
        }
      };
    } catch (error: any) {
      console.error('❌ Error fetching system roles:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch system roles'
      };
    }
  },

  /**
   * Check if current user has a specific permission
   */
  async checkPermission(resource: string, action: string): Promise<{
    success: boolean;
    hasPermission: boolean;
    reason?: string;
  }> {
    try {
      const response = await apiClient.post('/api/v1/admin/check-permission', {
        resource,
        action
      });

      if (response.data.success) {
        return {
          success: true,
          hasPermission: response.data.hasPermission || false,
          reason: response.data.reason
        };
      } else {
        return {
          success: false,
          hasPermission: false,
          reason: response.data.error?.message
        };
      }
    } catch (error: any) {
      console.error('❌ Error checking permission:', error);
      return {
        success: false,
        hasPermission: false,
        reason: 'Permission check failed'
      };
    }
  },

  /**
   * Get audit logs
   */
  async getAuditLogs(filters?: {
    page?: number;
    limit?: number;
    adminId?: string;
    action?: string;
    resource?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    success: boolean;
    data?: {
      logs: AuditLog[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.adminId) queryParams.append('adminId', filters.adminId);
      if (filters?.action) queryParams.append('action', filters.action);
      if (filters?.resource) queryParams.append('resource', filters.resource);
      if (filters?.startDate) queryParams.append('startDate', filters.startDate);
      if (filters?.endDate) queryParams.append('endDate', filters.endDate);

      const url = `/api/v1/admin/audit-logs${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await apiClient.get(url);

      if (response.data.success) {
        // Backend returns: { success: true, data: [...], pagination: {...} }
        return {
          success: true,
          data: {
            logs: response.data.data || [],
            pagination: {
              page: response.data.pagination?.page || 1,
              limit: response.data.pagination?.limit || 50,
              total: response.data.pagination?.total || 0,
              totalCount: response.data.pagination?.total || 0, // Support both field names
              totalPages: response.data.pagination?.totalPages || 0,
            }
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to fetch audit logs'
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching audit logs:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to fetch audit logs'
      };
    }
  },
};
